
import { expand, map, reduce } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';


//Imports
import { AsyncControls, AsyncStorage } from "@core/services/AsyncStorage";
import { Category, Product, ProductCategoryData } from '@core/data/product';
import { APIResponse } from '@shared/types.barrel';
import { ClientsService, Client } from '@admin/clients/clients.service';
import { DebugService as debug } from "@core/services/debug.service";
import { AuthService } from '@core/services/auth/auth.service';

//Exports
export { AsyncStorage } from "@core/services/AsyncStorage";
export { Category, Product } from '@core/data/product';

@Injectable({
    providedIn: 'root'
})
export class ProductsService {

    private _products!: AsyncStorage<Product>;
    private _categories!: AsyncStorage<Category>;
    private _activeProductsClientId: BehaviorSubject<string> = new BehaviorSubject<string>("");
    private _activeCategoriesClientId: BehaviorSubject<string> = new BehaviorSubject<string>("");
    private _targetClientId: string = "";
    private _isInitialConfiguration: boolean = false;

    private _eTag: string | null = null;

    constructor(
        private _http: HttpClient,
        private _clientsService: ClientsService,
        private _auth: AuthService
    ) {
        this._initializeStorage();
    }

    private _initializeStorage() {

        //Categories
        this._categories = new AsyncStorage(this._http);
        this._categories._setActiveClientBehaviourSubject(this._activeProductsClientId);

        //Products
        this._products = new AsyncStorage(this._http);
        this._products._setActiveClientBehaviourSubject(this._activeCategoriesClientId);
        this._products._setAddFunction(this._saveProductConfiguration());
        this._products._setElementComparisonFunction(this._productComparison);
        this._products._setSaveActiveElementFunction(this._saveProductConfiguration());
        this._products._setUpdateFunction(this._listProducts());


        this._clientsService.clients.onActiveElementChange()
            .subscribe((client: Client) => {
                this._targetClientId = client.clientId;
                this._categories.update();
                this._products.update();
            });
    }

    public get activeProductsClientId(): string { return this._activeProductsClientId.value };
    public get activeCategoriesClientId(): string { return this._activeCategoriesClientId.value };

    public get products() { return this._products };
    public get categories() { return this._categories };

    private _productComparison(a: Product | null, b: Product | null): boolean {
        //Compare the two product IDs
        return a && b ? a.productId === b.productId : false;
    }

    private _saveProductConfiguration(): (_: any, options: any) => Observable<any> {
        const self = this;
        return (_: any, options: any): Observable<any> => {

            let productsAndCategories: ProductCategoryData = new ProductCategoryData();

            //Set the client id
            productsAndCategories.clientId = self._targetClientId || self._auth.activeClient?.clientId;

            //Add products
            let products: Product[] = self._products.data.value ?? [];
            productsAndCategories.productData.products = [];
            for (let i in products) {
                let productId = products[i].productId;
                productsAndCategories.productData[productId] = products[i];
                productsAndCategories.productData.products.push(productId);

                //Remove unnecessary fields
                if (products[i].supportedLocales) delete products[i].supportedLocales
            }

            //Add the ETag header if available
            const headers: Record<string, string> = {};
            if (self._eTag)
                headers["If-Match"] = self._eTag;

            //Select the appropriate endpoint
            let endpoint = "products/update"
            if (this._isInitialConfiguration)
                endpoint = "products/add"

            //Load all products
            debug.log(`Updating product configuration - endpoint: ${endpoint}, etag: ${this._eTag ?? "-"}`);
            return this._http
                .post<APIResponse<ProductCategoryData>>(endpoint, productsAndCategories, { headers, observe: "response" })
                .pipe(
                    map((res: HttpResponse<APIResponse<ProductCategoryData>>) => {

                        //Handle errors
                        const errors = res.body?.errors
                        if (errors?.length) {
                            throw new Error(errors.pop())
                        }

                        //Copy the etag value
                        const etag = res.headers.get("ETag")
                        if (etag)
                            this._eTag = etag

                        //Upon a valid response the initial configuration has been created
                        if (this._isInitialConfiguration)
                            this._isInitialConfiguration = false;

                        //Parse the products
                        let products: Product[] = [];
                        const productData = res.body?.data?.productData;
                        const productIds = [...new Set(productData?.products ?? []).values()]
                        for (let productId of productIds) {
                            products.push(<Product>productData?.[productId])
                        }

                        return products
                    })
                )
        }
    }

    private _listProducts(): (options: any, asyncControls?: AsyncControls) => Observable<Product[]> {

        const self = this;
        return (options: any, asyncControls?: AsyncControls): Observable<Product[]> => {

            //Load all products
            const clientId = options?.clientId || this._targetClientId || this._auth.activeClient?.clientId
            return this._http
                .post<APIResponse<ProductCategoryData>>("products/get", { clientId }, { observe: "response" })
                .pipe(
                    // expand keeps calling until condition fails
                    expand((res: HttpResponse<APIResponse<ProductCategoryData>>) => {

                        //Check for errors
                        const errors = res.body?.errors
                        if (errors?.length)
                            throw new Error(errors[0])

                        //Check if there are more products to load
                        const lastEvaluatedKey = (<any>res).body?.data.lastEvaluatedKey
                        if (!lastEvaluatedKey)
                            return of()

                        return self._http.post<APIResponse<ProductCategoryData>>("products/get", { clientId, lastEvaluatedKey }, { observe: "response" })

                    }),
                    map((res: HttpResponse<APIResponse<ProductCategoryData>>) => {
                        if (asyncControls?.activeClient)
                            asyncControls.activeClient.next(this._targetClientId)

                        //Update the local client id record
                        this._activeProductsClientId.next(this._targetClientId)

                        //Copy the etag value
                        const etag = res.headers.get("etag")
                        if (etag)
                            this._eTag = etag

                        //Check if this is the initial configuration for products
                        const isClientId = !!res.body?.data?.clientId
                        if (!isClientId) this._isInitialConfiguration = true;

                        //Parse the products
                        let products: Product[] = [];
                        const productData = res.body?.data?.productData;
                        const productIds = [...new Set(productData?.products ?? []).values()]
                        for (let productId of productIds) {
                            products.push(<Product>productData?.[productId])
                        }

                        debug.log(`Loaded products:`, { clientId, etag });

                        return products
                    }),
                    reduce((all, current) => all.concat(current), [] as Product[])
                );
        }

    }

}
